import { FunctionalComponent, h, Fragment } from "preact";
import styles from "./categoryTagsStyles.css";
import { useAppSelector } from "../../../../../redux/hooks";
import { useEffect, useState } from "preact/hooks";
import logger from "../../../../../logger";

interface IProps {
  handleCheckboxChange: (
    category: string,
    nestedKey: string,
    nestedValue: unknown
  ) => void;
  selectedCategory: Array<{ nestedKey: string; nestedValue: unknown }>;
}

const CategoryTags: FunctionalComponent<IProps> = ({
  handleCheckboxChange,
  selectedCategory,
}) => {
  const search = useAppSelector((state) => state.search);
  const alias = useAppSelector((state) => state.alias);

  const [sum, setSum] = useState(0);
  const [categories, setCategories] = useState<Record<string, any>>({});
  const [openCategories, setOpenCategories] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Check if a checkbox is selected
  const isChecked = (nestedKey: string, nestedValue: unknown) => {
    return selectedCategory?.some(
      (item) => item.nestedKey === nestedKey && item.nestedValue === nestedValue
    );
  };

  /** ---------------------------
   * Utility: Aggregate items by key
   * --------------------------- */
const aggregateCategory = (items: any[]) => {
    const agg: Record<string, number> = {};
    for (const item of items) {
        const key = Object.keys(item)[0];
        const count = item[key];
        agg[key] = (agg[key] || 0) + count;
    }
    // Convert aggregated results back into an array of single-key objects, sorted by count descending
    return Object.entries(agg)
      .sort((a, b) => b[1] - a[1])
      .map(([key, count]) => ({ [key]: count }));
};

/** ---------------------------
 * Utility: Sort top-level by first inner key name
 * --------------------------- */
const sortCategoriesByInnerKey = (data: Record<string, any> = {}) => {
    const clone = { ...data };

    const getFirstInnerKey = (val: any) => {
        if (Array.isArray(val) && val.length > 0) {
            const first = val.find((it) => typeof it === "object" && it !== null);
            if (first)
                return String(Object.keys(first)[0] || "")
                    .trim()
                    .toLowerCase();
        } else if (typeof val === "object" && val !== null) {
            return String(Object.keys(val)[0] || "")
                .trim()
                .toLowerCase();
        }
        return "";
    };

    const sortedTopKeys = Object.keys(clone).sort((a, b) => {
        const aKey = getFirstInnerKey(clone[a]) || a.toLowerCase();
        const bKey = getFirstInnerKey(clone[b]) || b.toLowerCase();
        return aKey.localeCompare(bKey, undefined, { sensitivity: "base" });
    });

    return sortedTopKeys.reduce((acc: Record<string, any>, topKey) => {
        const items = clone[topKey];
        if (Array.isArray(items)) {
            acc[topKey] = [...items].sort((x, y) => {
                const kx = String(Object.keys(x)[0] || "")
                    .trim()
                    .toLowerCase();
                const ky = String(Object.keys(y)[0] || "")
                    .trim()
                    .toLowerCase();
                return kx.localeCompare(ky, undefined, { sensitivity: "base" });
            });
        } else if (typeof items === "object" && items !== null) {
            acc[topKey] = sortCategoriesByInnerKey(items);
        } else {
            acc[topKey] = items;
        }
        return acc;
    }, {});
};

/** ---------------------------
 * Calculate sum of all numeric values in categories
 * --------------------------- */
useEffect(() => {
    if (search?.categories) {
        const total = Object.values(search.categories).reduce(
            (acc: number, value) => {
                if (Array.isArray(value)) {
                    return (
                        acc +
                        value.reduce((nestedAcc, item) => {
                            return (
                                nestedAcc +
                                Object.values(item).reduce(
                                    (itemAcc: any, itemValue) =>
                                        itemAcc + (typeof itemValue === "number" ? itemValue : 0),
                                    0
                                )
                            );
                        }, 0)
                    );
                } else if (typeof value === "object" && value !== null) {
                    return (
                        acc +
                        Object.values(value).reduce(
                            (nestedAcc, nestedValue) =>
                                nestedAcc +
                                (typeof nestedValue === "number" ? nestedValue : 0),
                            0
                        )
                    );
                }
                return acc;
            },
            0
        );

        setSum(total);
    }
}, [search?.categories]);

/** ---------------------------
 * Process & sort categories
 * --------------------------- */
useEffect(() => {
    if (!search?.categories) return;

    const categoriesObj = { ...search.categories }; // clone to avoid mutation

    // Aggregate wafer_size
    if (categoriesObj.wafer_size) {
        const wafers = aggregateCategory(categoriesObj.wafer_size);
        if (wafers.length > 0) {
            categoriesObj.wafer_size = wafers;
        }
    }

    // Aggregate type
    if (categoriesObj.type) {
        categoriesObj.type = aggregateCategory(categoriesObj.type);
    }

    const sorted = sortCategoriesByInnerKey(categoriesObj);
    setCategories(sorted);

    logger.log({
        message: "Categories processed and sorted",
        portal_alias: alias.portalAlias,
        search_query: search.lastQ,
        categories: sorted,
    });
}, [search?.categories]);

  /** ---------------------------
   * Filter categories by search term
   * --------------------------- */
  const filterCategories = (data: Record<string, any>, query: string) => {
    if (!query.trim()) return sortCategoriesByInnerKey(data);

    const filtered: Record<string, any> = {};

    Object.entries(data).forEach(([arrayName, arrayItems]) => {
      if (Array.isArray(arrayItems)) {
        const matchedItems = arrayItems.filter((item) =>
          Object.entries(item).some(
            ([key, value]) =>
              key.toLowerCase().includes(query.toLowerCase()) ||
              String(value).toLowerCase().includes(query.toLowerCase())
          )
        );
        if (matchedItems.length > 0) {
          filtered[arrayName] = matchedItems;
        }
      } else if (typeof arrayItems === "object" && arrayItems !== null) {
        const nestedFiltered = filterCategories(arrayItems, query);
        if (Object.keys(nestedFiltered).length > 0) {
          filtered[arrayName] = nestedFiltered;
        }
      }
    });

    return sortCategoriesByInnerKey(filtered);
  };

  const filteredCategories = filterCategories(categories, searchTerm);

  /** ---------------------------
   * JSX Render
   * --------------------------- */
  return (
    <div>
      {Object.entries(filteredCategories).length > 0 && (
        <>
          <div className={styles.upperflexBox}>
            <div className={styles.flexBox}>
              <svg
                fill="#000000"
                height="12px"
                width="12px"
                viewBox="0 0 330 330"
                xmlSpace="preserve"
                onClick={() => setOpenCategories(!openCategories)}
                style={{
                  transform: openCategories ? "rotate(90deg)" : "",
                  cursor: "pointer",
                }}
              >
                <path
                  d="M250.606,154.389l-150-149.996c-5.857-5.858-15.355-5.858-21.213,0.001 
                    c-5.857,5.858-5.857,15.355,0.001,21.213l139.393,139.39L79.393,304.394
                    c-5.857,5.858-5.857,15.355,0.001,21.213
                    C82.322,328.536,86.161,330,90,330s7.678-1.464,10.607-4.394
                    l149.999-150.004c2.814-2.813,4.394-6.628,4.394-10.606 
                    C255,161.018,253.42,157.202,250.606,154.389z"
                ></path>
              </svg>
              <p className={styles.subTitle}>Categories:</p>
            </div>
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onInput={(e) =>
                setSearchTerm((e.target as HTMLInputElement).value)
              }
            />
          </div>
        </>
      )}

      {openCategories &&
        Object.entries(filteredCategories as Record<string, unknown>).map(
          ([arrayName, arrayItems]) => (
            <div key={arrayName}>
              {/* Display array name as heading */}
              <p className={styles.heading}>{arrayName.replace(/_/g, " ")}</p>
              <dl className={styles.no_dots}>
                {/* Iterate over items within the array */}
                {Array.isArray(arrayItems) &&
                  arrayItems.map((item, index) => (
                    <dl className={styles.no_dots}>
                      {/* Render key-value pairs of each item */}
                      {Object.entries(item as Record<string, unknown>).map(
                        ([nestedKey, nestedValue]) => (
                          <li key={nestedKey}>
                            <span>
                              <input
                                type="checkbox"
                                checked={isChecked(nestedKey, nestedValue)} // Check if item is selected
                                onClick={() =>
                                  handleCheckboxChange(
                                    arrayName,
                                    nestedKey,
                                    nestedValue
                                  )
                                }
                              />
                              {nestedKey}
                            </span>
                            {String(nestedValue)}
                          </li>
                        )
                      )}
                    </dl>
                  ))}
              </dl>
            </div>
          )
        )}
    </div>
  );
};

export default CategoryTags;
